import { Icon } from "@mg/dali/src";
import { Check } from "@phosphor-icons/react";
import {
  Button,
  Flex,
  Grid,
  TextField,
  IconButton,
  Box,
  Dialog as RadixDialog,
  Tooltip,
  Text,
} from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import { useState, useRef, type FormEvent, useEffect, useMemo } from "react";
import React from "react";

import { AvatarWithInitials } from "./AvatarWithInitials";

import { useUI } from "../contexts/ui";
import { useUploadProgress } from "../hooks/useUploadProgress";
import { uploadAvatar } from "../services/upload";
import {
  updateMinimalUserProfile,
  checkUsernameUniqueness,
} from "../services/userProfile";
import { useAppSelector } from "../utils/hooks";
import { useCurrentUserProfile } from "../utils/queries/userProfile";

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: any; // The user being edited, if different from current user
}

export function EditProfileDialog({
  open,
  onOpenChange,
  user,
}: EditProfileDialogProps) {
  const { notify } = useUI();
  const currentUser = useAppSelector((state) => state.auth.value);
  // Always load via the live `/private/profile` endpoint. The legacy
  // `useProfile`-via-`getDesigner` pipeline is a no-op stub left over
  // from the Mar 2026 my-network removal and silently returns null,
  // which made `if (!profile) return null` below short-circuit the
  // entire dialog — opening the avatar dropdown and clicking
  // "Edit Profile" appeared to do nothing for share-link-minted
  // CATALYST_REQUESTER users (and in fact every user). Switching to
  // the same query the onboarding flow already uses unblocks the
  // dialog without granting any new permissions.
  const { data: profile } = useCurrentUserProfile();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const checkRequestId = useRef(0);

  // Upload progress state
  const { uploadState, startUpload, setError, completeUpload, resetUpload } =
    useUploadProgress();

  // Track initial profile for change detection
  const initialProfile = useRef<any>(null);
  useEffect(() => {
    if (profile) {
      initialProfile.current = {
        name: profile.name || "",
        preferredName: profile.preferredName || "",
        avatar: profile.avatar || undefined,
      };
    }
  }, [profile, open]);

  // Reset upload state when dialog closes
  useEffect(() => {
    if (!open) {
      resetUpload();
    }
  }, [open, resetUpload]);

  // Create a debounced version of the username check
  const debouncedCheckUsername = useMemo(
    () =>
      debounce(async (value: string) => {
        if (!value || value === profile?.preferredName) {
          setIsCheckingUsername(false);
          return;
        }

        // Increment request ID to track the latest request
        const requestId = ++checkRequestId.current;

        try {
          setIsCheckingUsername(true);
          const { isUnique } = await checkUsernameUniqueness(value);

          // Only update state if this is still the latest request
          if (requestId === checkRequestId.current) {
            if (!isUnique) {
              setUsernameError(
                "This display name is already taken in your enterprise",
              );
            } else {
              setUsernameError(null);
            }
          }
        } catch (error) {
          // Only update state if this is still the latest request
          if (requestId === checkRequestId.current) {
            console.error("Error checking username:", error);
            setUsernameError(
              "Unable to check username availability. Please try again.",
            );
          }
        } finally {
          // Only update loading state if this is still the latest request
          if (requestId === checkRequestId.current) {
            setIsCheckingUsername(false);
          }
        }
      }, 1000),
    [profile?.preferredName],
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedCheckUsername.cancel();
    };
  }, [debouncedCheckUsername]);

  // Validate name format
  const validateName = (value: string) => {
    if (!value) {
      setNameError("Name is required");
      return false;
    }
    if (value.length < 2) {
      setNameError("Name must be at least 2 characters long");
      return false;
    }
    if (!/^[a-zA-Z\s\-']+$/.test(value)) {
      setNameError(
        "Name can only contain letters, spaces, hyphens, and apostrophes",
      );
      return false;
    }
    setNameError(null);
    return true;
  };

  // Handle name change
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    validateName(value);
  };

  // Validate username format
  const validateUsername = (value: string) => {
    // If empty, that's valid (username is optional)
    if (!value) {
      setUsernameError(null);
      return true;
    }

    if (value.length < 3) {
      setUsernameError("Display name must be at least 3 characters long");
      return false;
    }
    if (value.length > 15) {
      setUsernameError("Display name must be 15 characters or less");
      return false;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
      setUsernameError(
        "Display name can only contain letters, numbers, hyphens, and underscores",
      );
      return false;
    }
    // Check for consecutive special characters
    if (/[-_]{2,}/.test(value)) {
      setUsernameError(
        "Display name cannot contain consecutive hyphens or underscores",
      );
      return false;
    }
    // Check for starting/ending with special characters
    if (/^[-_]|[-_]$/.test(value)) {
      setUsernameError(
        "Display name cannot start or end with a hyphen or underscore",
      );
      return false;
    }
    return true;
  };

  // Handle username change
  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);

    // Clear any existing errors
    setUsernameError(null);

    // Only proceed with validation if a value is provided
    if (value) {
      if (validateUsername(value)) {
        // Cancel any pending checks before starting a new one
        debouncedCheckUsername.cancel();
        setIsCheckingUsername(true);
        debouncedCheckUsername(value);
      }
    } else {
      // If empty, clear any checking state
      setIsCheckingUsername(false);
      debouncedCheckUsername.cancel();
    }
  };

  // Detect if any field has changed
  const isDirty = useMemo(() => {
    if (!initialProfile.current) return false;
    return (
      name !== initialProfile.current.name ||
      username !== initialProfile.current.preferredName ||
      avatarUrl !== initialProfile.current.avatar
    );
  }, [name, username, avatarUrl]);

  // Check if there are any validation errors
  const hasErrors = useMemo(() => {
    return Boolean(nameError || usernameError || isCheckingUsername);
  }, [nameError, usernameError, isCheckingUsername]);

  // Sync state with profile when dialog opens or profile changes
  useEffect(() => {
    if (profile) {
      setName(profile.name || "");
      setUsername(profile.preferredName || "");
      setAvatarUrl(profile.avatar || undefined);
    }
  }, [profile, open]);

  const profileMutation = useMutation({
    mutationFn: updateMinimalUserProfile,
    onSuccess: () => {
      onOpenChange(false);
      notify({
        title: "Success",
        message: "Profile updated successfully",
      });
    },
    onError: (error: any) => {
      console.error("Profile update error:", error);
      if (error.message?.includes("Display name is already taken")) {
        setUsernameError(
          "This display name is already taken in your enterprise",
        );
      } else {
        notify({
          title: "Error",
          message: "Failed to update profile",
          autoDismiss: false,
        });
      }
    },
  });

  const handleAvatarUpload = async (e: FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (target.files == null || !target.files.length || !currentUser?.userID) {
      return;
    }

    const file = target.files[0];
    startUpload(file.name, file.size);

    try {
      const avatarKey = await uploadAvatar(file);
      setAvatarUrl(avatarKey);
      completeUpload();
    } catch (err: any) {
      setError(err.data?.message || err.message || "Upload failed");
      notify({
        title: "Oops! Something went wrong",
        message: err.data?.message || err.message,
        autoDismiss: false,
      });
    }
  };

  const handleRemovePhoto = () => {
    setAvatarUrl(undefined);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validateName(name) || !validateUsername(username)) {
      return;
    }
    const minimalPayload = {
      avatar: avatarUrl || "",
      name,
      preferredName: username,
    };
    profileMutation.mutate(minimalPayload);
  };

  if (!profile) return null;

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Content
        size="2"
        width="600px"
        className="bg-white dark:bg-neutral-900 grid gap-6 rounded-xl p-8 shadow-xl"
        data-testid="edit-profile-dialog-content"
      >
        <Box>
          <RadixDialog.Title>
            Edit {user ? `${user.name}&apos;s` : "your"} profile
          </RadixDialog.Title>
          <RadixDialog.Description>
            <span className="sr-only">
              Edit {user ? `${user.name}&apos;s` : "your"} profile information
              and avatar.
            </span>
          </RadixDialog.Description>
        </Box>
        <form onSubmit={handleSubmit} className="grid gap-6">
          <Grid columns="1fr 220px" gap="6" align="start">
            {/* Left: Form fields */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <label htmlFor="edit-profile-fullname" className="font-medium">
                  Full name
                </label>
                <TextField.Root
                  id="edit-profile-fullname"
                  value={name}
                  onChange={handleNameChange}
                  placeholder="Full name"
                  required
                  color={nameError ? "red" : undefined}
                  className={nameError ? "border-red-500" : ""}
                />
                {nameError && (
                  <Text size="1" color="red" className="mt-1">
                    {nameError}
                  </Text>
                )}
                <div className="mt-1 space-y-1">
                  <Text size="1" color="gray" className="block">
                    Name requirements:
                  </Text>
                  <ul className="text-neutral-500 list-disc space-y-0.5 pl-4 text-xs">
                    <li>At least 2 characters long</li>
                    <li>
                      Only letters, spaces, hyphens (-), and apostrophes
                      (&apos;)
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="edit-profile-displayname"
                  className="font-medium"
                >
                  Display name (optional)
                </label>
                <TextField.Root
                  id="edit-profile-displayname"
                  value={username}
                  onChange={handleUsernameChange}
                  placeholder="Display name (optional)"
                  color={usernameError ? "red" : undefined}
                  className={usernameError ? "border-red-500" : ""}
                />
                {usernameError && (
                  <Text size="1" color="red" className="mt-1">
                    {usernameError}
                  </Text>
                )}
                {isCheckingUsername && (
                  <Text
                    size="1"
                    color="gray"
                    className="mt-1 flex items-center gap-1"
                  >
                    <Icon.CircleNotch className="animate-spin" size={12} />
                    Checking availability...
                  </Text>
                )}
                <div className="mt-1 space-y-1">
                  <Text size="1" color="gray" className="block">
                    Display name requirements (if provided):
                  </Text>
                  <ul className="text-neutral-500 list-disc space-y-0.5 pl-4 text-xs">
                    <li>3-15 characters long</li>
                    <li>
                      Letters, numbers, hyphens (-), and underscores (_) only
                    </li>
                    <li>
                      No consecutive or leading/trailing special characters
                    </li>
                    <li>Must be unique within your enterprise</li>
                  </ul>
                </div>
              </div>
            </div>
            {/* Right: Avatar */}
            <div className="flex flex-col items-center gap-4">
              <div className="bg-neutral-200 dark:bg-neutral-800 relative flex size-32 items-center justify-center overflow-visible rounded-full">
                {uploadState.isUploading ? (
                  <div className="flex size-32 flex-col items-center justify-center rounded-full bg-carbon-300">
                    <Icon.CircleNotch className="animate-spin" size={60} />
                  </div>
                ) : avatarUrl ? (
                  <>
                    <img
                      src={
                        avatarUrl
                          ? `https://static.puntt.ai/${avatarUrl}`
                          : undefined
                      }
                      alt="Profile"
                      className="border-white size-full rounded-full border-4 object-cover"
                    />
                    <div className="absolute -right-3 -top-3 z-50">
                      <Tooltip content="Remove avatar">
                        <IconButton
                          size="1"
                          color="red"
                          variant="solid"
                          onClick={handleRemovePhoto}
                          aria-label="Remove avatar"
                          disabled={uploadState.isUploading}
                        >
                          <Icon.X size={16} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </>
                ) : (
                  <AvatarWithInitials
                    size={32}
                    name={name}
                    avatar={undefined}
                  />
                )}
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleAvatarUpload}
                disabled={uploadState.isUploading}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => avatarInputRef.current?.click()}
                className="w-full"
                disabled={uploadState.isUploading}
              >
                Upload Avatar
              </Button>
            </div>
          </Grid>
          <Flex gap="2" justify="end" className="mt-6">
            <RadixDialog.Close>
              <Button variant="soft" color="gray" type="button">
                Cancel
              </Button>
            </RadixDialog.Close>
            <Button
              variant="solid"
              type="submit"
              disabled={
                !isDirty ||
                uploadState.isUploading ||
                profileMutation.isPending ||
                hasErrors
              }
            >
              {profileMutation.isPending ? (
                <Icon.CircleNotch className="mr-2 animate-spin" size={18} />
              ) : (
                <Check />
              )}
              Save
            </Button>
          </Flex>
        </form>
      </RadixDialog.Content>
    </RadixDialog.Root>
  );
}
